import { ActiveTab } from '@/constants/chat'
import { UserRole } from '@/constants/room'
import {
  Negotiation,
  NegotiationMember,
  NegotiationType,
} from '@/redux/types/negotiationWebsocket'
import { Advisor } from '@/redux/types/advisor'
import { ChatbotType } from '@/redux/types/configuration'
import {
  Negotiation as RoomNegotiation,
  RoleChoice,
  RoomMember,
  Status,
} from '@/redux/types/room'
import {
  findRoomNegotiationById,
  mapRoomNegotiationToWs,
} from '@/utils/negotiationMapper'
import { hasLeftGroupChat } from '@/utils/groupChatMembers'
import { TeamChatbot } from '@/redux/types/teamChatbot'
import { SERVER_URL } from '@/api/config'
import {
  AvatarDescriptor,
  SidebarTab,
  TabKind,
} from '@/components/OGE/Tabbar/sidebarTabTypes'

type BuildTabsParams = {
  userRole?: string
  userEmail?: string
  showTeamRoomTab: boolean
  isNegotiationPhase: boolean
  teamDummyAssignment: {
    botName?: string
    teamId?: number
  } | null
  activeNegotiations: Negotiation[]
  showDecisionTab: boolean
  showSummaryTab: boolean
  showCharterPrepTab: boolean
  showCharterGroupTab: boolean
  advisorUnreadCount: number
  aiPlayerUnreadCount: number
  teamRoomUnreadCount: number
  negotiationUnreadById: Record<string, number>
  advisors: Advisor[]
  teamChatbots: TeamChatbot[]
  isRandRPhase: boolean
  isAdvisorPhase: boolean
  roomMembers: RoomMember[]
  playerTeamId?: number | null
  isProfessorViewingPlayer?: boolean
  isProfessorViewingTeam?: boolean
  negotiationIdFromURL?: string
  roomNegotiations?: RoomNegotiation[]
  getTabLabel: (negotiation: Negotiation) => string
  getProfessorTabLabel: (negotiation: Negotiation) => string
}

const memberToAvatar = (member: {
  name?: string
  email?: string
  profile_picture?: string
  member_profile_picture?: string
  member_name?: string
  member_email?: string
}): AvatarDescriptor => {
  const name =
    member.name ||
    member.member_name ||
    member.email?.split('@')[0] ||
    member.member_email?.split('@')[0] ||
    '?'
  const pic = member.profile_picture || member.member_profile_picture
  return {
    src: pic ? `${SERVER_URL}${pic}` : undefined,
    fallback: name.slice(0, 2).toUpperCase(),
  }
}

const negotiationMemberAvatars = (
  negotiation: Negotiation,
  userEmail?: string
): AvatarDescriptor[] => {
  const isGroup = negotiation.negotiation_type === NegotiationType.GROUP_ROOM
  const members = (negotiation.members || []).filter(
    (m: NegotiationMember) =>
      m.email !== userEmail && !(isGroup && hasLeftGroupChat(m))
  )
  return members.map((m) =>
    memberToAvatar({
      name: m.name,
      email: m.email,
      profile_picture: m.profile_picture,
    })
  )
}

const teamMemberAvatars = (
  roomMembers: RoomMember[],
  teamId: number | null | undefined
): AvatarDescriptor[] => {
  if (teamId == null) return []
  return teamPlayersOnTeam(roomMembers, teamId).map((m) => memberToAvatar(m))
}

const teamPlayersOnTeam = (
  roomMembers: RoomMember[],
  teamId: number
): RoomMember[] =>
  roomMembers.filter(
    (m) =>
      m.teamId === teamId &&
      m.role === RoleChoice.PLAYER &&
      m.invitation_status === Status.Accepted
  )

const chatbotToAvatar = (name: string): AvatarDescriptor => ({
  src: '/images/avatar-chatbot.svg',
  fallback: name.slice(0, 2).toUpperCase(),
})

const advisorTabAvatars = (
  advisors: Advisor[],
  teamChatbots: TeamChatbot[],
  isRandRPhase: boolean,
  isAdvisorPhase: boolean
): AvatarDescriptor[] => {
  const avatars: AvatarDescriptor[] = []

  if (isRandRPhase) {
    const randrAdvisor = advisors.find(
      (advisor) => advisor.chatbottype === ChatbotType.RANDR
    )
    if (randrAdvisor) {
      avatars.push(chatbotToAvatar(randrAdvisor.chatbotname))
    }
  }

  if (isAdvisorPhase) {
    for (const chatbot of teamChatbots) {
      avatars.push(chatbotToAvatar(chatbot.chatbot_name))
    }
  }

  if (!isRandRPhase && !isAdvisorPhase) {
    const randrAdvisor = advisors.find(
      (advisor) => advisor.chatbottype === ChatbotType.RANDR
    )
    if (randrAdvisor) {
      avatars.push(chatbotToAvatar(randrAdvisor.chatbotname))
    }
    for (const chatbot of teamChatbots) {
      avatars.push(chatbotToAvatar(chatbot.chatbot_name))
    }
  }

  return avatars
}

const classifyNegotiation = (negotiation: Negotiation): TabKind => {
  if (negotiation.negotiation_type === NegotiationType.PUBLIC_ROOM) {
    return 'publicRoom'
  }
  if (negotiation.negotiation_type === NegotiationType.GROUP_ROOM) {
    return 'group'
  }
  return 'conference'
}

export const buildSidebarTabs = (params: BuildTabsParams): SidebarTab[] => {
  const {
    userRole,
    userEmail,
    showTeamRoomTab,
    isNegotiationPhase,
    teamDummyAssignment,
    activeNegotiations,
    showDecisionTab,
    showSummaryTab,
    showCharterPrepTab,
    showCharterGroupTab,
    advisorUnreadCount,
    aiPlayerUnreadCount,
    teamRoomUnreadCount,
    negotiationUnreadById,
    advisors,
    teamChatbots,
    isRandRPhase,
    isAdvisorPhase,
    roomMembers,
    playerTeamId,
    isProfessorViewingPlayer,
    isProfessorViewingTeam,
    negotiationIdFromURL,
    roomNegotiations = [],
    getTabLabel,
    getProfessorTabLabel,
  } = params

  if (userRole === UserRole.Professor) {
    const channelTabs: SidebarTab[] = []
    const negotiationTabs: SidebarTab[] = []

    if (isProfessorViewingPlayer) {
      channelTabs.push({
        id: ActiveTab.Advisor,
        label: 'Player Advisor Chat',
        section: 'channels',
        kind: 'advisor',
        avatars: advisorTabAvatars(
          advisors,
          teamChatbots,
          isRandRPhase,
          isAdvisorPhase
        ),
      })
    }

    if (isProfessorViewingTeam) {
      negotiationTabs.push({
        id: ActiveTab.TeamRoom,
        label: 'Team Room',
        section: 'negotiations',
        kind: 'teamRoom',
        avatars: teamMemberAvatars(roomMembers, playerTeamId),
        unreadCount: teamRoomUnreadCount,
      })
    }

    if (negotiationIdFromURL) {
      const wsMatch = activeNegotiations.find(
        (n) => String(n.negotiationId) === negotiationIdFromURL
      )
      const roomMatch = findRoomNegotiationById(
        roomNegotiations,
        negotiationIdFromURL
      )
      const match =
        wsMatch ??
        (roomMatch ? mapRoomNegotiationToWs(roomMatch) : undefined)

      if (match) {
        const kind = classifyNegotiation(match)
        negotiationTabs.push({
          id: String(match.negotiationId),
          label: getProfessorTabLabel(match),
          section: 'negotiations',
          kind,
          negotiation: match,
          avatars: negotiationMemberAvatars(match),
          unreadCount:
            negotiationUnreadById[String(match.negotiationId)] ?? 0,
        })
      } else {
        negotiationTabs.push({
          id: negotiationIdFromURL,
          label: 'Loading...',
          section: 'negotiations',
          kind: 'conference',
        })
      }
    }

    return [...channelTabs, ...negotiationTabs]
  }

  const tabs: SidebarTab[] = [
    {
      id: ActiveTab.Advisor,
      label: ActiveTab.Advisor,
      section: 'channels',
      kind: 'advisor',
      unreadCount: advisorUnreadCount,
      avatars: advisorTabAvatars(
        advisors,
        teamChatbots,
        isRandRPhase,
        isAdvisorPhase
      ),
    },
  ]

  if (showCharterPrepTab) {
    tabs.push({
      id: ActiveTab.CharterPrep,
      label: 'Preparation Coach',
      section: 'channels',
      kind: 'charterPrep',
    })
  }

  if (showCharterGroupTab) {
    tabs.push({
      id: ActiveTab.CharterGroup,
      label: 'Group Moderator',
      section: 'channels',
      kind: 'charterGroup',
    })
  }

  if (showDecisionTab) {
    tabs.push({
      id: ActiveTab.Decision,
      label: 'Decision',
      section: 'channels',
      kind: 'decision',
    })
  }

  if (showSummaryTab) {
    tabs.push({
      id: ActiveTab.Summary,
      label: 'Summary',
      section: 'channels',
      kind: 'summary',
    })
  }

  if (showTeamRoomTab) {
    tabs.push({
      id: ActiveTab.TeamRoom,
      label: 'Team Room',
      section: 'negotiations',
      kind: 'teamRoom',
      avatars: teamMemberAvatars(roomMembers, playerTeamId),
      unreadCount: teamRoomUnreadCount,
    })
  }

  if (isNegotiationPhase && teamDummyAssignment) {
    const currentMember = userEmail
      ? roomMembers.find((m) => m.member_email === userEmail)
      : undefined
    const studentAvatars: AvatarDescriptor[] = [
      { src: '/images/avatar-chatbot.svg', fallback: 'AI' },
    ]
    if (currentMember) {
      studentAvatars.push(memberToAvatar(currentMember))
    }
    tabs.push({
      id: ActiveTab.DummyLLMChat,
      label: teamDummyAssignment.botName || 'AI Player',
      section: 'negotiations',
      kind: 'aiPlayer',
      unreadCount: aiPlayerUnreadCount,
      avatars: studentAvatars,
    })
  }

  for (const negotiation of activeNegotiations) {
    const kind = classifyNegotiation(negotiation)
    tabs.push({
      id: String(negotiation.negotiationId),
      label: getTabLabel(negotiation),
      section: 'negotiations',
      kind,
      negotiation,
      avatars: negotiationMemberAvatars(negotiation, userEmail),
      unreadCount:
        negotiationUnreadById[String(negotiation.negotiationId)] ?? 0,
    })
  }

  return tabs
}
